import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useScore } from '../hooks/useScore';
import { useContent } from '../contexts/ContentContext';
import { useTooltip } from '../contexts/TooltipContext';
import { createPortal } from 'react-dom';
import { useDialogLayer } from '../hooks/useDialogLayer';
import GuideHelpButton from './GuideHelpButton';

interface MagnifierProps {
    imageUrl: string;
    onClose: () => void;
    imageId?: string;
}

const Magnifier: React.FC<MagnifierProps> = ({ imageUrl, onClose, imageId }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [zoom, setZoom] = useState<number>(1);
    const [minZoom, setMinZoom] = useState(0.1);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });

    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const zoomRef = useRef(zoom);
    const minZoomRef = useRef(minZoom);
    const { sendAction } = useScore();
    const { content, language, t } = useContent();
    const overlayRef = useRef<HTMLDivElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    const { showTooltip, hideTooltip } = useTooltip();

    const handleClose = useCallback(() => {
        hideTooltip();
        onClose();
    }, [hideTooltip, onClose]);
    useDialogLayer(overlayRef, true, handleClose);

    const maxZoom = 10;
    const zoomFactor = 0.1;

    useEffect(() => {
        zoomRef.current = zoom;
    }, [zoom]);

    useEffect(() => {
        minZoomRef.current = minZoom;
    }, [minZoom]);

    useEffect(() => {
        if (imageId) {
            sendAction(imageId, 'zoom');
        }
    }, [imageId, sendAction]);


    useEffect(() => {
        return () => {
            hideTooltip();
        };
    }, [hideTooltip]);

    const resetImage = useCallback(() => {
        if (!imageRef.current || !containerRef.current) return;

        const image = imageRef.current;
        const container = containerRef.current;

        const imageAspectRatio = image.naturalWidth / image.naturalHeight;
        const containerAspectRatio = container.clientWidth / container.clientHeight;

        let initialZoom;
        if (imageAspectRatio > containerAspectRatio) {
            // Image is wider than container, fit to width
            initialZoom = container.clientWidth / image.naturalWidth;
        } else {
            // Image is taller than or same aspect as container, fit to height
            initialZoom = container.clientHeight / image.naturalHeight;
        }

        setZoom(initialZoom);
        setMinZoom(initialZoom);
        setTranslate({ x: 0, y: 0 });
    }, []);


    const handleImageLoad = () => {
        setIsLoading(false);
        resetImage();
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
        setLastPosition({ x: e.clientX, y: e.clientY });
        e.currentTarget.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        const deltaX = e.clientX - lastPosition.x;
        const deltaY = e.clientY - lastPosition.y;
        setLastPosition({ x: e.clientX, y: e.clientY });
        setTranslate(prev => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
    };

    const handleMouseUpOrLeave = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        e.currentTarget.style.cursor = 'grab';
    };

    const handleWheel = useCallback((e: WheelEvent) => {
        e.preventDefault();
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        // Calculate mouse position relative to the center of the container
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;

        const currentZoom = zoomRef.current;
        const currentMinZoom = minZoomRef.current;
        const delta = e.deltaY > 0 ? -zoomFactor : zoomFactor;
        const newZoom = Math.max(currentMinZoom, Math.min(maxZoom, currentZoom + delta * currentZoom));

        // If we are zooming out and reach minZoom (or very close to it), reset position
        if (newZoom <= currentMinZoom * 1.01) {
            setZoom(currentMinZoom);
            setTranslate({ x: 0, y: 0 });
            return;
        }

        const zoomRatio = newZoom / currentZoom;

        setTranslate(prev => ({
            x: x - (x - prev.x) * zoomRatio,
            y: y - (y - prev.y) * zoomRatio
        }));
        setZoom(newZoom);
    }, []);

    // Reset on window resize
    useEffect(() => {
        window.addEventListener('resize', resetImage);
        return () => window.removeEventListener('resize', resetImage);
    }, [resetImage]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [handleWheel]);

    return createPortal(
        <div ref={overlayRef} role="dialog" aria-modal="true" aria-label={t('detail.magnified')} className="magnifier-overlay" onClick={handleClose} onKeyDown={event => {
            if (event.key === '+' || event.key === '=') setZoom(value => Math.min(maxZoom, value * 1.1));
            if (event.key === '-') setZoom(value => Math.max(minZoom, value / 1.1));
            if (event.key.startsWith('Arrow')) { event.preventDefault(); setTranslate(value => ({ x: value.x + (event.key === 'ArrowLeft' ? 30 : event.key === 'ArrowRight' ? -30 : 0), y: value.y + (event.key === 'ArrowUp' ? 30 : event.key === 'ArrowDown' ? -30 : 0) })); }
        }}>
            <div className="absolute top-0 left-0 w-full bg-black/70 text-white p-4 pr-36 z-50 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-lg font-bold">
                    {content.ui[language].imageDetail.viewingId.replace('{id}', imageId || '')}
                </div>
                <div className="text-sm opacity-80 mt-1">
                    {content.tooltips[language].imageDetail.magnifierInstruction}
                </div>
            </div>
            <div className="absolute top-6 right-20 z-50 text-white"><GuideHelpButton section="image-zoom" /></div>
            <div
                ref={containerRef}
                className="magnifier-image-container"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the image container
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUpOrLeave}
                onMouseLeave={handleMouseUpOrLeave}
                style={{ cursor: 'grab' }}
            >
                {isLoading && <div className="magnifier-loader">{t('common.loading')}</div>}
                {failed && <p role="alert" className="text-white p-4">{t('common.imageFetchError')}</p>}
                <img
                    ref={imageRef}
                    src={imageUrl}
                    alt={t('detail.magnified')}
                    className="magnifier-image"
                    style={{
                        visibility: isLoading || failed ? 'hidden' : 'visible',
                        transform: `translate(${translate.x}px, ${translate.y}px) scale(${zoom})`,
                    }}
                    onLoad={handleImageLoad}
                    onError={() => { setIsLoading(false); setFailed(true); }}
                />
                {!isLoading && !failed && (
                    <div className="magnifier-zoom-display">
                        {t('detail.zoom', { zoom: (zoom / minZoom).toFixed(2) })}
                    </div>
                )}
            </div>
            <button
                ref={closeRef}
                aria-label={t('common.close')}
                className="magnifier-close-btn"
                onClick={handleClose}
                onMouseEnter={(e) => showTooltip(content.tooltips[language].imageDetail.magnifierClose, e)}
                onMouseLeave={hideTooltip}
            >
                <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    aria-hidden="true"
                    focusable="false"
                >
                    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
            </button>
        </div>, document.body
    );
};

export default Magnifier;
